import { BUDGET_TRUSTWORTHINESS_THRESHOLD } from '@/config';
import { checkBudget } from '@/optimizer/helpers/checkBudget';
import { formatNaturalDemand } from '@/optimizer/helpers/formatNaturalDemand';
import { formatOptimal } from '@/optimizer/helpers/formatOptimal';
import { getPeriod } from '@/optimizer/helpers/getPeriod';
import { modifyDailyPlan } from '@/optimizer/helpers/modifyDailyPlan';
import { modifyMonthlyPlan } from '@/optimizer/helpers/modifyMonthlyPlan';
import { modifyWeeklyPlan } from '@/optimizer/helpers/modifyWeeklyPlan';
import { prepareRules } from '@/optimizer/helpers/prepareRules';
import {
    createOptimizerServiceManager as createOptWasmOptimizerServiceManager,
    type OptimizerPort,
    type SerializableTable,
    getColumn,
    getColumnValues,
    vectorMean,
} from '@analytical-alley/opt-wasm';
import { Table, tableFromArrays } from 'apache-arrow';
import moment from 'moment/moment';
import { Rule } from './types';

function reconstructTable(serialized: SerializableTable): Table {
    // Reconstruct an Arrow Table from serialized format using tableFromArrays
    return tableFromArrays(serialized.data);
}

const buildOptimizationOutput = ({ plan, medias }: any) => {
    const monthlyTable = reconstructTable(plan.monthly);
    const optimizedPlanTable = reconstructTable(plan.optimizedPlan);
    const weeklyPlanTable = reconstructTable(plan.weekly);
    const dailyPlanTable = reconstructTable(plan.daily);

    const totalBudgetValues = plan.monthly.data['Total budget'] || [];
    
    const roiCol = getColumn(optimizedPlanTable, 'ROI');
    const monthlyAverage = vectorMean(roiCol);

    return {
        ...formatOptimal(plan.optimal, plan.refinedSpends),
        monthly: totalBudgetValues as number[],
        monthlyAverage,
        monthlyPlan: modifyMonthlyPlan({
            monthly: monthlyTable,
            optimizedPlan: optimizedPlanTable,
            weekly: weeklyPlanTable,
            daily: dailyPlanTable,
        } as any),
        dailyPlan: modifyDailyPlan(
            {
                optimizedPlan: optimizedPlanTable,
                monthly: monthlyTable,
                weekly: weeklyPlanTable,
                daily: dailyPlanTable,
            } as any,
            medias,
        ),
        weekDayPlan: modifyWeeklyPlan({
            weekly: weeklyPlanTable,
            optimizedPlan: optimizedPlanTable,
            monthly: monthlyTable,
            daily: dailyPlanTable,
        } as any),
        weekDayAverage: monthlyAverage,
        weeklyPlan: modifyWeeklyPlan({
            weekly: weeklyPlanTable,
            optimizedPlan: optimizedPlanTable,
            monthly: monthlyTable,
            daily: dailyPlanTable,
        } as any),
        weeklyAverage: monthlyAverage,
    };
};

export type OptimizerServiceManagerReturn = Awaited<
    ReturnType<typeof createOptimizerServiceManager>
>;

export const createOptimizerServiceManager = async (
    optimizerService: OptimizerPort,
) => {
    return createOptWasmOptimizerServiceManager<Rule, any>(optimizerService, {
        budgetTrustworthinessThreshold: BUDGET_TRUSTWORTHINESS_THRESHOLD,
        parseDate: (date: string) => moment(date, 'DD-MM-YYYY').toDate(),
        getPeriod,
        checkBudget,
        prepareRules,
        formatNaturalDemand,
        buildOptimizationOutput,
        getDurationRatio: (startDate: string, endDate: string) => {
            const durationInDays = moment(endDate, 'DD-MM-YYYY').diff(
                moment(startDate, 'DD-MM-YYYY'),
                'days',
            ) + 1;
            const yearDays = 366;

            return durationInDays / yearDays;
        },
    });
};
